import React, {useEffect, useState} from 'react';
import _ from 'lodash';
import {useSelector} from 'react-redux';
import {additionalData} from '../../../../../store/reducers/additionalData';
import clsx from 'clsx';
import {getConfig} from '../../../../../util/configUtil';
import {useMultiStyles} from './multiEntriesStyle';
import {useTranslation} from 'react-i18next';
import AttendanceChip from '../../../../../components/notes/component/attendanceChip';
import InformationBox from '../../../../../components/other/informationBox';
import {Grid, IconButton} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Chip from '@mui/material/Chip';
import OngoingSpellProgress from '../../../../../components/admissions/ongoingSpellProgress';
import moment from 'moment';
import {ctxIdFormToLoad} from '../../../../../store/reducers/patientContext';
import {agyleAssessmentList} from '../../../../../store/reducers/dicts';
import {SEPSIS_TOOL_TYPE} from '../../../../ward/forms/formsUtil';

const MultiEntriesActivityBox = ({patient, form, selectedForm, onSelectForm, previousPatientActivity}) => {
    const classes = useMultiStyles();
    const {t} = useTranslation();

    // Selectors
    const patientAddData = useSelector(additionalData);
    const loadForm = useSelector(ctxIdFormToLoad);
    const assessmentList = useSelector(agyleAssessmentList);

    // Configs
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');

    const [assessment, setAssessment] = useState(null);
    const [listOfForms, setListOfForms] = useState([]);

    const isActive = _.isNil(patient?.admission?.endDate);
    const [expanded, setExpanded] = useState(isActive);

    useEffect(() => {
        if (form) {
            const assessment = _.find(assessmentList, f => f.code === form.id);
            setAssessment(assessment);
        }
    }, [form, assessmentList]);

    useEffect(() => {
        if (form && assessment?.metadata) {
            const {dataPath, dataSaveDestination} = form.options;
            if (dataPath) {
                const {path} = assessment.metadata;
                switch (dataSaveDestination) {
                    case 'patientData': {
                        const data = _.get(patientAddData, `other.${dataPath}`);
                        const filteredByActivity = _.filter(data, f => {
                            const activityId = f?.attendanceId;
                            const date = _.get(f, path.date);

                            if (activityId) {
                                return patient?.admission?.__id === activityId;
                            }

                            // Check if the provided date falls within the admission period.
                            // - `startDate`: The start date of the patient's admission.
                            // - `isActive`: A boolean indicating if the admission is still active (i.e., no end date).
                            // - `endDate`: The end date of the admission. If the admission is active, it uses the start date of the previous patient activity as the end date.
                            // Returns `true` if the date is within the range [startDate, endDate], inclusive; otherwise, `false`.
                            if (date) {
                                const startDate = moment(patient?.admission?.startDate);
                                const isActive = _.isNil(patient?.admission?.endDate) || previousPatientActivity;
                                const endDate = isActive
                                    ? moment(previousPatientActivity?.admission?.startDate || undefined)
                                    : moment();

                                return moment(date).isBetween(startDate, endDate, null, '[]');
                            }
                            return null;
                        });
                        const sortedByDate = _.orderBy(filteredByActivity, [path.date], ['desc']);
                        setListOfForms(sortedByDate || []);
                        break;
                    }
                    case 'piw':
                    default: {
                        const data = _.get(patient, dataPath);
                        setListOfForms(data?.list || []);
                        break;
                    }
                }
            } else {
                clearStates();
                console.warn(`For this form, the 'dataPath' has not been configured in the form options.`);
            }
        } else {
            clearStates();
        }
    }, [patientAddData, form, assessment]);

    useEffect(() => {
        if (!_.isEmpty(listOfForms) && loadForm) {
            const findForm = _.find(listOfForms, f => f.id === loadForm || f.__id === loadForm);
            if (findForm) {
                onSelectForm(findForm);
            }
        }
    }, [listOfForms, loadForm]);

    const clearStates = () => {
        setListOfForms([]);
        onSelectForm(null);
    };

    const getElementId = () => {
        if (form?.options?.dataPath === 'sepsisTool') {
            return '__id';
        }
        return 'id';
    };

    const getIcon = (element) => {
        if (form?.options?.showScore) {
            const score = _.get(element, form?.options?.scorePath);
            if (!score) return null;

            return (
                <div className={classes.scoreBox}>
                    {score}
                </div>
            );
        }
        return null;
    };

    const checkData = (data, paths) => {
        const getData = _.get(data, paths[0]);
        return getData || _.get(data, paths[1]);
    };

    const renderHistoryList = () => {
        if (!_.isEmpty(listOfForms)) {
            return listOfForms.map(element => {
                let isCanceled = element?.canceled;
                if (form?.id === 'sepsisTool') {
                    isCanceled = element?.status === SEPSIS_TOOL_TYPE.CANCELED
                }
                const id = getElementId();
                const isSelected = selectedForm?.[id] === element?.[id];

                const {path} = assessment.metadata;
                const user = _.isArray(path.user) ? checkData(element, path.user) : _.get(element, path.user);
                const date = _.isArray(path.user) ? checkData(element, path.date) : _.get(element, path.date);

                return (
                    <div
                        key={element?.[id]}
                        onClick={() => onSelectForm(element)}
                        className={clsx(
                            classes.sectionItem,
                            isSelected && 'selected',
                            isCanceled && 'canceled',
                        )}
                    >
                        <Grid container direction="row" spacing={1} alignItems="center">
                            <Grid item xs={6}>
                                <div>
                                     <span>
                                        {`${t('Created by')} `}
                                     </span>

                                    <span style={{fontWeight: 'bold'}}>
                                       {user}
                                     </span>
                                </div>
                                <div>
                                     <span>
                                        {`${t('Created')}: `}
                                     </span>

                                    <span style={{fontWeight: 'bold'}}>
                                        {moment(date).format(defaultDateTimeFormat)}
                                     </span>
                                </div>
                            </Grid>

                            <Grid item xs={6} style={{display: 'flex', justifyContent: 'flex-end'}}>
                                {getIcon(element)}
                            </Grid>
                        </Grid>
                    </div>
                );
            });
        }
        return (
            <span>
                {t('There are no historical records for this form')}
            </span>
        );
    };

    const handleExpand = () => {
        setExpanded(!expanded);
    };

    return (
        <>
            <div className={classes.multiRoot}>
                <AttendanceChip admission={patient.admission}/>

                <IconButton
                    size="small"
                    onClick={handleExpand}
                >
                    {expanded ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                </IconButton>

                {!_.isEmpty(patient.admission?.episodes) &&
                    <Chip
                        label={`${patient.admission?.episodes?.length || 0} ${t('Ep.')}`}
                        variant="outlined"
                    />
                }

                {isActive && <OngoingSpellProgress/>}
            </div>

            {expanded &&
                <>
                    {listOfForms?.length > 0 && renderHistoryList()}
                    {!listOfForms?.length > 0 &&
                        <InformationBox type="info">
                            {t('There is no data to display.')}
                        </InformationBox>
                    }
                </>
            }
        </>
    );
};

export default MultiEntriesActivityBox;